/**
 * The Autodesk-only pieces of the review card ("Led AI - Daily Report
 * Editor", review screen). The card itself is shared with Procore; these
 * replace the static kind badge with the daily-report section the record
 * fills, and show the template question a jobsite-event record answers.
 *
 * A daily-report record can move between sections before review (the
 * backend's convert RPC). An issue cannot become a report entry or change
 * its own kind here — Autodesk issues keep their type.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import {
  ChevronDown,
  ClipboardCheck,
  Eye,
  FileText,
  HardHat,
  Truck,
  Wrench,
} from "lucide-react";

import type { AutodeskReviewDestinations } from "../../server/queries/getReviewQueue";
import { postReviewConvert, type ConsoleRow, type ReviewPhotoResponse } from "../api";
import { EvidenceTile } from "../components/chrome";
import { ADR, C, FONT_MONO } from "../tokens";
import { entryBullets, AutodeskGlyph, AutodeskStyles } from "./AutodeskDailyReport";
import { KindChip, type ChipKind } from "./ReviewCardParts";

const LABEL = ADR.label;

type Kind = ChipKind;

const TABLE_KINDS: Record<string, Kind> = {
  manpower: { key: "manpower", chip: "Manpower", menu: "Manpower", icon: HardHat },
  work_logs: { key: "work_logs", chip: "Manpower", menu: "Manpower", icon: HardHat },
  deliveries: { key: "deliveries", chip: "Delivery", menu: "Deliveries", icon: Truck },
  equipment: { key: "equipment", chip: "Equipment", menu: "Equipment", icon: Wrench },
};
const EVENT_KIND: Omit<Kind, "key" | "menu"> = { chip: "Jobsite event", icon: ClipboardCheck };
const NOTES_KIND: Kind = { key: "notes", chip: "Notes", menu: "Notes", icon: FileText };
const ISSUE_KIND: Kind = { key: "issue", chip: "Issue", menu: "Issue", icon: Eye };

/** An observation or punch item is an Autodesk issue; everything else is a
 *  daily-report entry. */
export function isAutodeskIssue(item: ConsoleRow): boolean {
  return item.recordType === "observation" || item.kind === "observation" || item.kind === "punch" || item.category === "observations";
}

function kindOf(item: ConsoleRow, destinations: AutodeskReviewDestinations): Kind {
  // The phone's ACC type chip: an Issue wears its record's own glyph (an
  // observation's eye, a punch item's triangle); a report entry the log's.
  if (isAutodeskIssue(item)) return { ...ISSUE_KIND, kind: item.kind === "punch" ? "punch" : "observation" };
  return { ...reportKindOf(item, destinations), kind: "log" };
}

function reportKindOf(item: ConsoleRow, destinations: AutodeskReviewDestinations): Kind {
  const category = item.category ?? "notes";
  if (TABLE_KINDS[category]) return TABLE_KINDS[category]!;
  const question = destinations.questions.find((entry) => entry.category === category);
  if (question) return { key: category, menu: shortQuestion(question.label), ...EVENT_KIND };
  return NOTES_KIND;
}

/** "Were any inspections logged?" → "Inspections". */
function shortQuestion(label: string): string {
  const match = /(?:were any|was a|any)\s+(.+?)(?:\s+(?:logged|completed|reported))?\??$/i.exec(label.trim());
  const text = (match?.[1] ?? label).replace(/['’]s log$/i, "s").replace(/\s+issues$/i, "s");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function AutodeskKindChip({ item, destinations, search, disabled, onConverted }: {
  item: ConsoleRow;
  destinations: AutodeskReviewDestinations;
  search: string;
  disabled?: boolean;
  onConverted: () => void;
}) {
  const current = kindOf(item, destinations);
  const issue = current.key === ISSUE_KIND.key;
  const sections: Array<{ heading?: string; kinds: Kind[] }> = issue
    ? [{ kinds: [current] }]
    : [
        { kinds: dedupe(destinations.tables.map((table) => TABLE_KINDS[table.category] ?? { key: table.category, chip: table.label, menu: table.label, icon: FileText })) },
        ...(destinations.questions.length > 0
          ? [{ heading: destinations.questions[0]!.sectionLabel, kinds: destinations.questions.map((question) => ({ key: question.category, menu: shortQuestion(question.label), ...EVENT_KIND })) }]
          : []),
        { kinds: [NOTES_KIND] },
      ];
  return (
    <KindChip
      testId="autodesk-kind-chip"
      current={current}
      menuLabel={issue ? "Issues can’t change type in Autodesk" : "Daily report section"}
      sections={sections}
      disabled={disabled}
      onPick={async (kind) => {
        if (issue) return null;
        const result = await postReviewConvert(search, { sourceKind: "log", sourceId: item.id, targetKind: "log", targetSubcategory: kind.key });
        if (!result.ok) return result.error || "Could not move this entry.";
        onConverted();
        return null;
      }}
    />
  );
}

/** The chip's word for a record — also what a decided one is folded under. */
export function autodeskKindLabel(item: Pick<ConsoleRow, "kind" | "category" | "recordType">, destinations: AutodeskReviewDestinations): string {
  return kindOf(item as ConsoleRow, destinations).chip;
}

/** The chip a record wears, for its folded row. */
export function autodeskKindOf(item: Pick<ConsoleRow, "kind" | "category" | "recordType">, destinations: AutodeskReviewDestinations): Kind {
  return kindOf(item as ConsoleRow, destinations);
}

function dedupe(kinds: Kind[]): Kind[] {
  const seen = new Set<string>();
  return kinds.filter((kind) => {
    const key = kind.menu;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * What the record puts on the day's Form when it is the answer to a template
 * question: the question, the answer accepting it gives, and the note and
 * photos that go with that answer. Collapsed until asked for.
 */
export function AutodeskAnswerPreview({ item, destinations, photos }: {
  item: ConsoleRow;
  destinations: AutodeskReviewDestinations;
  photos: ReviewPhotoResponse[];
}) {
  const [open, setOpen] = useState(false);
  if (isAutodeskIssue(item)) return null;
  const question = destinations.questions.find((entry) => entry.category === item.category);
  if (!question) return null;
  const toggle = question.valueKind === "toggle";
  const options = toggle ? ["Yes", "No", "NA"] : question.presets;
  const answer = toggle ? "Yes" : undefined;
  const images = photos.filter((photo) => photo.type === "image");
  return (
    <div data-testid="autodesk-answer-preview" style={{ marginTop: 20, border: "1px solid rgba(20,17,12,.08)", borderRadius: 14, overflow: "hidden", background: ADR.recess }}>
      <AutodeskStyles />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="adr-hov"
        style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: "11px 14px", border: 0, background: "transparent", cursor: "pointer", textAlign: "left", font: "inherit" }}
      >
        <AutodeskGlyph size={14} />
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>Autodesk preview</span>
        {!open && <span style={{ fontSize: 12, color: LABEL, whiteSpace: "nowrap" }}>{shortQuestion(question.label)}{answer ? ` · ${answer}` : ""} · {images.length} {images.length === 1 ? "photo" : "photos"}</span>}
        <span style={{ display: "inline-flex", transition: "transform .18s", transform: open ? "rotate(180deg)" : "none" }}><ChevronDown size={13} color={C.sub} aria-hidden /></span>
      </button>
      {open && (
        <div style={{ borderTop: "1px solid rgba(20,17,12,.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px" }}>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
              {question.label}{question.required && <span style={{ color: C.red }}> *</span>}
            </span>
            {options.length > 0 && (
              <span aria-label="Answer" style={{ display: "inline-flex", padding: 2, borderRadius: 8, background: ADR.track }}>
                {options.map((option) => (
                  <span key={option} style={{ height: 26, padding: "0 12px", borderRadius: 6, display: "inline-flex", alignItems: "center", fontSize: 12.5, fontWeight: 600, ...(option === answer ? { background: ADR.paper, color: C.ink, boxShadow: "0 1px 2px rgba(20,17,12,.12)" } : { color: C.sub }) }}>{option}</span>
                ))}
              </span>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 150px", gap: 14, padding: "0 14px 14px" }}>
            <Recess label="Note">
              <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: 13, lineHeight: 1.55, color: C.ink }}>
                {entryBullets(item).map((bullet, index) => <li key={index}>{bullet}</li>)}
              </ul>
            </Recess>
            <Recess label={`Photos · ${images.length}`}>
              {images.length === 0 ? (
                <span style={{ display: "block", marginTop: 8, fontSize: 12, color: C.sub2 }}>None attached</span>
              ) : (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {images.slice(0, 4).map((photo) => (
                    <span key={photo.id} style={{ width: 52, height: 52, borderRadius: 7, overflow: "hidden", display: "block" }}>
                      <EvidenceTile jobSiteId={item.jobSiteId} url={photo.thumbnailUrl ?? photo.url} height={52} alt="" />
                    </span>
                  ))}
                </div>
              )}
            </Recess>
          </div>
        </div>
      )}
    </div>
  );
}

function Recess({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ background: ADR.card, border: "1px solid rgba(20,17,12,.07)", borderRadius: 10, padding: "10px 12px" }}>
      <span style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: "1.2px", color: LABEL, textTransform: "uppercase" }}>{label}</span>
      {children}
    </div>
  );
}

/** `?page=items&view=log&site=…&day=…` — the day's report, from review. */
export function dailyReportHref(search: string, jobSiteId: string, startedAt: string | undefined): string {
  const params = new URLSearchParams(search);
  for (const key of ["reviewing", "reviewItem", "project", "walk", "item", "cat", "needsok"]) params.delete(key);
  params.set("page", "items");
  params.set("view", "log");
  params.set("site", jobSiteId);
  if (startedAt) {
    const date = new Date(startedAt);
    if (!Number.isNaN(date.getTime())) {
      params.set("day", `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`);
    }
  }
  return `?${params.toString()}`;
}
